import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Calendar as CalendarIcon, Users, MapPin, Wifi, Car, Utensils, Home, CreditCard, Building2, Copy, Check, Loader2, ChevronLeft, ChevronRight, X as XIcon, ZoomIn, MessageCircle, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { format, addDays, differenceInDays, eachDayOfInterval, parseISO } from "date-fns";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const { t, language, translateAmenity } = useLanguage();
  const [property, setProperty] = useState(null);
  const [checkInDate, setCheckInDate] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);
  const [numGuests, setNumGuests] = useState("2");
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [specialRequests, setSpecialRequests] = useState("");
  const [loading, setLoading] = useState(false);
  const [priceInfo, setPriceInfo] = useState(null);
  const [calculatingPrice, setCalculatingPrice] = useState(false);
  const [bookedDates, setBookedDates] = useState([]);
  const [disabledDays, setDisabledDays] = useState([]);
  
  // Payment states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [currentBooking, setCurrentBooking] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [bankInfo, setBankInfo] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState({ stripe_enabled: false, bank_transfer_enabled: false, bank_info: null });
  
  // Discount code states
  const [discountCode, setDiscountCode] = useState("");
  const [discountInfo, setDiscountInfo] = useState(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Contact form state
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });
  const [sendingContact, setSendingContact] = useState(false);

  useEffect(() => {
    fetchProperty();
    fetchBookedDates();
    fetchPaymentConfig();
    // Track page view
    axios.post(`${API}/analytics/track`, { page: "/", referrer: document.referrer }).catch(() => {});
  }, []);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`${API}/property`);
      const prop = response.data;
      setProperty(prop);

      // Update OG meta tags dynamically for crawlers that execute JS
      if (prop.name) document.querySelector('meta[property="og:title"]')?.setAttribute("content", prop.name);
      if (prop.description) document.querySelector('meta[property="og:description"]')?.setAttribute("content", prop.description.slice(0, 200));
      // Always use /api/og-image for social sharing (it returns 1200x630 cropped version)
    } catch (error) {
      console.error("Error fetching property:", error);
    }
  };

  const fetchBookedDates = async () => {
    try {
      const response = await axios.get(`${API}/bookings/dates`);
      const bookings = response.data.booked_dates || [];
      const blocked = response.data.blocked_dates || [];
      setBookedDates(bookings);
      
      // Create array of all disabled dates (bookings + blocked)
      const disabled = [];
      bookings.forEach(booking => {
        const start = parseISO(booking.check_in);
        const end = parseISO(booking.check_out);
        const daysInRange = eachDayOfInterval({ start, end: addDays(end, -1) });
        disabled.push(...daysInRange);
      });
      blocked.forEach(b => {
        const start = parseISO(b.start_date);
        const end = parseISO(b.end_date);
        const daysInRange = eachDayOfInterval({ start, end });
        disabled.push(...daysInRange);
      });
      setDisabledDays(disabled);
    } catch (error) {
      console.error("Error fetching booked dates:", error);
    }
  };

  const fetchPaymentConfig = async () => {
    try {
      const response = await axios.get(`${API}/payment-settings/public`);
      setPaymentConfig(response.data);
    } catch (error) {
      console.error("Error fetching payment config:", error);
    }
  };

  const isDateBooked = (date) => {
    return disabledDays.some(bookedDate => 
      bookedDate.toDateString() === date.toDateString()
    );
  };

  // Lightbox helpers
  const allImages = property ? property.images || [] : [];
  const openLightbox = (index) => { setLightboxIndex(index); setLightboxOpen(true); };
  const closeLightbox = () => setLightboxOpen(false);
  const prevImage = useCallback(() => setLightboxIndex(i => (i - 1 + allImages.length) % allImages.length), [allImages.length]);
  const nextImage = useCallback(() => setLightboxIndex(i => (i + 1) % allImages.length), [allImages.length]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") prevImage();
      if (e.key === "ArrowRight") nextImage();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightboxOpen, prevImage, nextImage]);

  const validateDiscountCode = async () => {
    if (!discountCode.trim()) {
      toast.error(language === "is" ? "Vinsamlegast sláðu inn afsláttarkóða" : "Please enter a discount code");
      return;
    }
    
    setValidatingDiscount(true);
    try {
      const response = await axios.post(`${API}/discount-codes/validate?code=${discountCode}`);
      setDiscountInfo(response.data);
      toast.success(language === "is" 
        ? `Afsláttarkóði virkur! ${response.data.discount_percent}% afsláttur` 
        : `Discount code valid! ${response.data.discount_percent}% off`);
    } catch (error) {
      setDiscountInfo(null);
      toast.error(error.response?.data?.detail || (language === "is" ? "Ógildur afsláttarkóði" : "Invalid discount code"));
    } finally {
      setValidatingDiscount(false);
    }
  };

  const removeDiscount = () => {
    setDiscountCode("");
    setDiscountInfo(null);
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    
    if (!checkInDate || !checkOutDate) {
      toast.error(t("selectDates"));
      return;
    }

    if (priceInfo && !priceInfo.meetsMinimum) {
      toast.error(t("minimumNightsRequired", { min: priceInfo.minimumStay }));
      return;
    }

    // Check if selected dates overlap with booked dates
    const selectedDays = eachDayOfInterval({ start: checkInDate, end: addDays(checkOutDate, -1) });
    const hasConflict = selectedDays.some(day => isDateBooked(day));
    if (hasConflict) {
      toast.error(t("datesNotAvailable") || "Selected dates are not available");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/bookings`, {
        guest_name: guestName,
        guest_email: guestEmail,
        guest_phone: guestPhone,
        check_in: format(checkInDate, "yyyy-MM-dd"),
        check_out: format(checkOutDate, "yyyy-MM-dd"),
        num_guests: parseInt(numGuests),
        special_requests: specialRequests,
        discount_code: discountInfo ? discountCode : null,
      });

      // Show payment modal instead of success message
      setCurrentBooking({
        id: response.data.id,
        total_price: response.data.total_price,
        discount: response.data.discount || 0,
        guest_name: guestName,
        guest_email: guestEmail
      });

      if (!paymentConfig.stripe_enabled && !paymentConfig.bank_transfer_enabled) {
        // No payment methods — treat as booking inquiry
        toast.success(language === "is" 
          ? "Bókunarfyrirspurn móttekin! Við munum hafa samband við þig fljótlega." 
          : language === "lt" 
            ? "Užsakymo užklausa gauta! Susisieksime su jumis netrukus." 
            : "Booking request received! We will contact you shortly.");
        resetBookingForm();
      } else {
        setShowPaymentModal(true);
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || t("bookingFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleCardPayment = async () => {
    if (!currentBooking) return;
    
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/payments/create-checkout`, {
        booking_id: currentBooking.id,
        payment_method: "card",
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Villa við greiðslu");
      setProcessingPayment(false);
    }
  };

  const handleBankTransfer = async () => {
    if (!currentBooking) return;
    
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/payments/bank-transfer`, {
        booking_id: currentBooking.id,
        payment_method: "bank_transfer",
        origin_url: window.location.origin
      });
      
      setBankInfo(response.data.bank_info);
      setPaymentMethod("bank");
      setProcessingPayment(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Villa við millifærslu");
      setProcessingPayment(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const resetBookingForm = () => {
    setGuestName("");
    setGuestEmail("");
    setGuestPhone("");
    setCheckInDate(null);
    setCheckOutDate(null);
    setNumGuests("2");
    setSpecialRequests("");
    setPriceInfo(null);
    setDiscountCode("");
    setDiscountInfo(null);
    setShowPaymentModal(false);
    setCurrentBooking(null);
    setPaymentMethod(null);
    setBankInfo(null);
    fetchBookedDates();
  };

  const handleContact = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error(language === "is" ? "Vinsamlegast fylltu út öll svæði" : "Please fill in all fields");
      return;
    }
    setSendingContact(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success(language === "is" ? "Skilaboð send! Við svörum eins fljótt og hægt er." : "Message sent! We'll reply as soon as possible.");
      setContactForm({ name: "", email: "", message: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "is" ? "Villa við að senda skilaboð" : "Failed to send message"));
    } finally {
      setSendingContact(false);
    }
  };

  const calculateNights = () => {
    if (checkInDate && checkOutDate) {
      const nights = differenceInDays(checkOutDate, checkInDate);
      return nights > 0 ? nights : 0;
    }
    return 0;
  };

  const fetchPriceEstimate = async () => {
    if (!checkInDate || !checkOutDate) {
      setPriceInfo(null);
      return;
    }

    const nights = calculateNights();
    if (nights <= 0) {
      setPriceInfo(null);
      return;
    }

    setCalculatingPrice(true);
    try {
      const response = await axios.get(`${API}/pricing`);
      const pricingRules = response.data;

      // Find applicable pricing rule
      let pricePerNight = 150; // Default price
      let minimumStay = 1;
      let ruleName = "Standard Rate";

      if (pricingRules && pricingRules.length > 0) {
        for (const rule of pricingRules) {
          const ruleStart = new Date(rule.start_date);
          const ruleEnd = new Date(rule.end_date);
          if (checkInDate >= ruleStart && checkInDate <= ruleEnd) {
            pricePerNight = rule.price_per_night;
            minimumStay = rule.minimum_stay;
            ruleName = rule.notes || "Special Rate";
            break;
          }
        }
      }

      const totalPrice = pricePerNight * nights;
      const meetsMinimum = nights >= minimumStay;

      setPriceInfo({
        pricePerNight,
        nights,
        totalPrice,
        minimumStay,
        meetsMinimum,
        ruleName,
      });
    } catch (error) {
      console.error("Error calculating price:", error);
      setPriceInfo(null);
    } finally {
      setCalculatingPrice(false);
    }
  };

  useEffect(() => {
    fetchPriceEstimate();
  }, [checkInDate, checkOutDate]);

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-lg font-body">{t("loading")}</div>
      </div>
    );
  }

  const heroImage = property.images && property.images.length > 0 
    ? property.images[0] 
    : "https://customer-assets.emergentagent.com/job_spain-booking/artifacts/j69tjtgo_IMG_4461.jpeg";

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl backdrop-saturate-150 border-b border-white/20 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-3 md:py-4">
          <div className="flex justify-between items-center gap-2">
            <a
              href="/admin/login"
              className="text-xs md:text-sm font-ui text-neutral-600 hover:text-primary-600 transition-colors duration-200 px-2 py-1 md:px-3 md:py-1.5 rounded-lg hover:bg-primary-50 shrink-0"
              data-testid="admin-link"
            >
              {t("admin")}
            </a>
            <div className="flex items-center gap-1 md:gap-2">
              <LanguageSwitcher />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => document.querySelector('[data-testid="contact-section"]')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="nav-contact-button"
                className="font-ui hover:text-primary text-xs md:text-sm px-2 md:px-4"
              >
                {language === "is" ? "Hafa samband" : language === "lt" ? "Susisiekite" : "Contact"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => document.getElementById('booking-form').scrollIntoView({ behavior: 'smooth' })}
                data-testid="nav-booking-button"
                className="font-ui hover:text-primary text-xs md:text-sm px-2 md:px-4"
              >
                {t("bookNow")}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-16 md:py-32 lg:py-40 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Luxury Spanish villa with pool"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 lg:px-12 text-center text-white">
          <h2 className="text-2xl sm:text-4xl md:text-6xl lg:text-7xl font-display font-bold tracking-tight leading-tight mb-4 md:mb-6 drop-shadow-lg">
            {property.name}
          </h2>
          <p className="text-base md:text-xl lg:text-2xl font-body mb-4 flex items-center justify-center gap-2">
            <MapPin className="w-5 h-5 md:w-6 md:h-6 shrink-0" />
            <span className="line-clamp-2">{property.location}</span>
          </p>
          <Button
            onClick={() => document.getElementById('booking-form').scrollIntoView({ behavior: 'smooth' })}
            className="bg-primary hover:bg-primary-600 hover:text-white text-white px-6 md:px-8 py-3 md:py-4 rounded-full font-ui font-medium tracking-wide shadow-lg shadow-primary-200/50 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 text-sm md:text-base"
            data-testid="hero-cta-button"
          >
            {t("checkAvailability")}
          </Button>
        </div>
      </section>

      {/* Property Overview */}
      <section className="py-12 md:py-20 lg:py-32" data-testid="property-overview">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 lg:gap-16">
            <div>
              <h3 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-heading font-semibold tracking-tight leading-tight mb-4 md:mb-6">
                {t("mediterraneanParadise")}
              </h3>
              <div className="text-base md:text-lg lg:text-xl font-body leading-relaxed text-muted-foreground whitespace-pre-line">
                {language === "is" && property.description_is 
                  ? property.description_is 
                  : language === "lt" && property.description_lt 
                    ? property.description_lt 
                    : property.description}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 md:gap-6">
              <div className="bg-white border border-neutral-200 rounded-xl p-4 md:p-6" data-testid="stat-bedrooms">
                <Home className="w-6 h-6 md:w-8 md:h-8 text-primary mb-2 md:mb-3" />
                <div className="text-2xl md:text-3xl font-display font-bold text-primary-600">{property.bedrooms}</div>
                <div className="text-xs md:text-sm font-ui text-muted-foreground uppercase tracking-wider">{t("bedrooms")}</div>
              </div>
              <div className="bg-white border border-neutral-200 rounded-xl p-4 md:p-6" data-testid="stat-bathrooms">
                <div className="text-2xl md:text-3xl font-display font-bold text-primary-600 mb-1">{property.bathrooms}</div>
                <div className="text-xs md:text-sm font-ui text-muted-foreground uppercase tracking-wider">{t("bathrooms")}</div>
              </div>
              <div className="bg-white border border-neutral-200 rounded-xl p-4 md:p-6" data-testid="stat-guests">
                <Users className="w-6 h-6 md:w-8 md:h-8 text-primary mb-2 md:mb-3" />
                <div className="text-2xl md:text-3xl font-display font-bold text-primary-600">{property.max_guests}</div>
                <div className="text-xs md:text-sm font-ui text-muted-foreground uppercase tracking-wider">{t("maxGuests")}</div>
              </div>
              <div className="bg-white border border-neutral-200 rounded-xl p-4 md:p-6" data-testid="stat-size">
                <div className="text-2xl md:text-3xl font-display font-bold text-primary-600 mb-1">{property.size_sqm}</div>
                <div className="text-xs md:text-sm font-ui text-muted-foreground uppercase tracking-wider">{t("sqm")}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Amenities */}
      <section className="py-12 md:py-20 lg:py-32 bg-muted/30" data-testid="amenities-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <h3 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-heading font-semibold tracking-tight leading-tight mb-8 md:mb-12 text-center">
            {t("amenitiesFeatures")}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6">
            {property.amenities.map((amenity, index) => {
              const getIcon = (name) => {
                if (name.toLowerCase().includes('wifi')) return <Wifi className="w-5 h-5 md:w-6 md:h-6 text-primary" />;
                if (name.toLowerCase().includes('car') || name.toLowerCase().includes('parking')) return <Car className="w-5 h-5 md:w-6 md:h-6 text-primary" />;
                if (name.toLowerCase().includes('kitchen')) return <Utensils className="w-5 h-5 md:w-6 md:h-6 text-primary" />;
                return <Home className="w-5 h-5 md:w-6 md:h-6 text-primary" />;
              };
              
              return (
                <div
                  key={index}
                  className="bg-white border border-neutral-200 rounded-lg md:rounded-xl p-3 md:p-5 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                  data-testid={`amenity-${index}`}
                >
                  <div className="mb-2 md:mb-3">{getIcon(amenity)}</div>
                  <h4 className="text-sm md:text-base font-heading font-semibold leading-tight">{translateAmenity(amenity)}</h4>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      {property.images && property.images.length > 1 && (
        <section className="py-12 md:py-20 lg:py-32" data-testid="gallery-section">
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
            <h3 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-heading font-semibold tracking-tight leading-tight mb-8 md:mb-12 text-center">
              {t("propertyGallery")}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
              {property.images.slice(1).map((image, index) => (
                <div
                  key={index}
                  className="relative overflow-hidden rounded-lg md:rounded-xl aspect-[4/3] group cursor-pointer"
                  data-testid={`gallery-image-${index}`}
                  onClick={() => openLightbox(index + 1)}
                >
                  <img
                    src={image}
                    alt={`${property.name} - Image ${index + 2}`}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                    <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 drop-shadow-lg" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox */}
      {lightboxOpen && allImages.length > 0 && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          data-testid="lightbox-overlay"
          onClick={closeLightbox}
          onTouchStart={(e) => { e.currentTarget._touchX = e.touches[0].clientX; }}
          onTouchEnd={(e) => {
            const diff = e.changedTouches[0].clientX - (e.currentTarget._touchX || 0);
            if (Math.abs(diff) > 50) { diff > 0 ? prevImage() : nextImage(); }
          }}
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-10 text-white/80 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            data-testid="lightbox-close"
          >
            <XIcon className="w-6 h-6" />
          </button>

          {/* Counter */}
          <div className="absolute top-4 left-4 z-10 text-white/70 text-sm font-ui bg-white/10 px-3 py-1.5 rounded-full" data-testid="lightbox-counter">
            {lightboxIndex + 1} / {allImages.length}
          </div>

          {/* Previous */}
          {allImages.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-2 md:left-6 z-10 text-white/80 hover:text-white p-2 md:p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              data-testid="lightbox-prev"
            >
              <ChevronLeft className="w-6 h-6 md:w-8 md:h-8" />
            </button>
          )}

          {/* Image */}
          <img
            src={allImages[lightboxIndex]}
            alt={`${property.name} - ${lightboxIndex + 1}`}
            className="max-w-[90vw] max-h-[85vh] object-contain select-none"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
          />

          {/* Next */}
          {allImages.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-2 md:right-6 z-10 text-white/80 hover:text-white p-2 md:p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              data-testid="lightbox-next"
            >
              <ChevronRight className="w-6 h-6 md:w-8 md:h-8" />
            </button>
          )}
        </div>
      )}

      {/* Location Map */}
      <section className="py-12 md:py-20 lg:py-32" data-testid="location-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <h3 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-heading font-semibold tracking-tight leading-tight mb-4 md:mb-8 text-center">
            {t("perfectLocation")}
          </h3>
          <p className="text-sm md:text-lg lg:text-xl font-body text-center text-muted-foreground mb-6 md:mb-12 max-w-3xl mx-auto px-2">
            {t("locationDescription")}
          </p>
          <div className="bg-white border border-neutral-200 rounded-xl md:rounded-2xl overflow-hidden shadow-lg">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3140.844!2d-0.7303!3d37.9144!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd6307b38d93e4f7%3A0x5c5876f752e82750!2sC.%20Parana%2C%202%2C%2003189%20Orihuela%2C%20Alicante%2C%20Spain!5e0!3m2!1sen!2s!4v1234567890"
              width="100%"
              height="300"
              className="md:h-[450px]"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Villa La Zenia Paradise Location"
            ></iframe>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mt-6 md:mt-12">
            <div className="text-center">
              <div className="text-lg md:text-2xl font-display font-bold text-primary-600 mb-1 md:mb-2">10-12 min</div>
              <div className="text-xs md:text-sm font-ui text-muted-foreground">{t("beach")}</div>
            </div>
            <div className="text-center">
              <div className="text-lg md:text-2xl font-display font-bold text-primary-600 mb-1 md:mb-2">15 min</div>
              <div className="text-xs md:text-sm font-ui text-muted-foreground">{t("boulevard")}</div>
            </div>
            <div className="text-center">
              <div className="text-lg md:text-2xl font-display font-bold text-primary-600 mb-1 md:mb-2">5-10 min</div>
              <div className="text-xs md:text-sm font-ui text-muted-foreground">{t("restaurants")}</div>
            </div>
            <div className="text-center">
              <div className="text-lg md:text-2xl font-display font-bold text-primary-600 mb-1 md:mb-2">45 min</div>
              <div className="text-xs md:text-sm font-ui text-muted-foreground">{t("airport")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section id="booking-form" className="py-12 md:py-20 lg:py-32" data-testid="booking-section">
        <div className="max-w-3xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="bg-white border-2 border-primary-100 rounded-xl md:rounded-2xl shadow-xl shadow-primary-100/50 p-4 md:p-8 lg:p-12">
            <h3 className="text-xl md:text-3xl lg:text-4xl font-heading font-semibold tracking-tight leading-tight mb-4 md:mb-8 text-center">
              {t("requestBooking")}
            </h3>

            {/* Booked dates legend */}
            {bookedDates.length > 0 && (
              <div className="mb-4 md:mb-6 p-3 md:p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-xs md:text-sm font-ui text-red-700 flex items-center gap-2">
                  <span className="w-2 h-2 md:w-3 md:h-3 bg-red-400 rounded-full shrink-0"></span>
                  {t("bookedDatesInfo") || "Red dates are already booked and unavailable"}
                </p>
              </div>
            )}

            <form onSubmit={handleBooking} data-testid="booking-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
                <div>
                  <Label className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                    {t("checkInDate")}
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={`w-full justify-start text-left font-normal px-4 py-3 h-auto ${!checkInDate && "text-muted-foreground"}`}
                        data-testid="form-input-checkin"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {checkInDate ? format(checkInDate, "dd/MM/yyyy") : t("selectDate") || "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={checkInDate}
                        onSelect={(date) => {
                          setCheckInDate(date);
                          if (checkOutDate && date >= checkOutDate) {
                            setCheckOutDate(null);
                          }
                        }}
                        disabled={(date) => 
                          date < new Date() || isDateBooked(date)
                        }
                        modifiers={{
                          booked: disabledDays,
                        }}
                        modifiersStyles={{
                          booked: { backgroundColor: '#fecaca', color: '#991b1b', textDecoration: 'line-through' }
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                    {t("checkOutDate")}
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={`w-full justify-start text-left font-normal px-4 py-3 h-auto ${!checkOutDate && "text-muted-foreground"}`}
                        data-testid="form-input-checkout"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {checkOutDate ? format(checkOutDate, "dd/MM/yyyy") : t("selectDate") || "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={checkOutDate}
                        onSelect={setCheckOutDate}
                        disabled={(date) => 
                          date <= (checkInDate || new Date()) || isDateBooked(date)
                        }
                        modifiers={{
                          booked: disabledDays,
                        }}
                        modifiersStyles={{
                          booked: { backgroundColor: '#fecaca', color: '#991b1b', textDecoration: 'line-through' }
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Discount Code */}
              <div className="mb-4 md:mb-6">
                <Label className="text-xs md:text-sm font-ui font-medium text-neutral-700 mb-1 md:mb-2 block">
                  {language === "is" ? "Afsláttarkóði (valfrjálst)" : language === "lt" ? "Nuolaidos kodas (neprivaloma)" : "Discount Code (optional)"}
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                    placeholder={language === "is" ? "Sláðu inn kóða" : "Enter code"}
                    className="uppercase text-sm"
                    disabled={!!discountInfo}
                    data-testid="input-discount-code"
                  />
                  {discountInfo ? (
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={removeDiscount}
                      className="shrink-0 text-red-600 hover:text-red-700"
                    >
                      ✕
                    </Button>
                  ) : (
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={validateDiscountCode}
                      disabled={validatingDiscount || !discountCode}
                      className="shrink-0"
                      data-testid="validate-discount-button"
                    >
                      {validatingDiscount ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        language === "is" ? "Staðfesta" : "Apply"
                      )}
                    </Button>
                  )}
                </div>
                {discountInfo && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-700 font-medium">
                      ✓ {discountInfo.discount_percent}% {language === "is" ? "afsláttur virkur!" : "discount applied!"}
                    </p>
                  </div>
                )}
              </div>

              {calculateNights() > 0 && (
                <div className="mb-4 md:mb-6">
                  {calculatingPrice ? (
                    <div className="p-3 md:p-4 bg-neutral-100 rounded-lg text-center">
                      <span className="text-xs md:text-sm font-ui text-neutral-600">{t("calculatingPrice")}</span>
                    </div>
                  ) : priceInfo ? (
                    <div className={`p-4 md:p-6 rounded-lg md:rounded-xl border-2 ${priceInfo.meetsMinimum ? 'bg-primary-50 border-primary-200' : 'bg-red-50 border-red-200'}`} data-testid="price-display">
                      <div className="flex justify-between items-start mb-3 md:mb-4">
                        <div>
                          <p className="text-xs md:text-sm font-ui text-neutral-600 mb-1">{priceInfo.ruleName}</p>
                          <p className="text-sm md:text-lg font-body text-neutral-800">
                            €{priceInfo.pricePerNight} × {priceInfo.nights} {priceInfo.nights === 1 ? t("night") : t("nights")}
                          </p>
                          {discountInfo && (
                            <p className="text-sm text-green-600 font-medium mt-1">
                              -{discountInfo.discount_percent}% {language === "is" ? "afsláttur" : "discount"}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-xs md:text-sm font-ui text-neutral-600 mb-1">{t("totalPrice")}</p>
                          {discountInfo ? (
                            <>
                              <p className="text-sm text-neutral-400 line-through">
                                €{priceInfo.totalPrice.toFixed(0)}
                              </p>
                              <p className="text-xl md:text-3xl font-display font-bold text-green-600">
                                €{(priceInfo.totalPrice * (1 - discountInfo.discount_percent / 100)).toFixed(0)}
                              </p>
                            </>
                          ) : (
                            <p className="text-xl md:text-3xl font-display font-bold text-primary-600">
                              €{priceInfo.totalPrice.toFixed(0)}
                            </p>
                          )}
                        </div>
                      </div>
                      {!priceInfo.meetsMinimum && (
                        <div className="pt-3 md:pt-4 border-t border-red-300">
                          <p className="text-xs md:text-sm font-ui text-red-700 font-medium">
                            ⚠️ {t("minimumStay")}: {priceInfo.minimumStay} {t("nights")}
                          </p>
                          <p className="text-xs text-red-600 mt-1">
                            {t("minimumStayWarning", { min: priceInfo.minimumStay })}
                          </p>
                        </div>
                      )}
                      {priceInfo.meetsMinimum && (
                        <div className="pt-3 md:pt-4 border-t border-primary-200">
                          <p className="text-xs font-ui text-primary-700">
                            ✓ {t("priceIncludes", { nights: priceInfo.nights, nightWord: priceInfo.nights === 1 ? t("night") : t("nights"), price: priceInfo.pricePerNight })}
                          </p>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              )}

              <div className="mb-4 md:mb-6">
                <Label htmlFor="guests" className="text-xs md:text-sm font-ui font-medium text-neutral-700 mb-1 md:mb-2 block">
                  {t("numberOfGuests")}
                </Label>
                <Select value={numGuests} onValueChange={setNumGuests}>
                  <SelectTrigger data-testid="form-select-guests" className="text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(property.max_guests)].map((_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>
                        {i + 1} {i === 0 ? t("guest") : t("guests")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-4 md:mb-6">
                <Label htmlFor="name" className="text-xs md:text-sm font-ui font-medium text-neutral-700 mb-1 md:mb-2 block">
                  {t("yourName")}
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  required
                  className="px-3 md:px-4 py-2 md:py-3 rounded-lg text-sm"
                  data-testid="form-input-name"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
                <div>
                  <Label htmlFor="email" className="text-xs md:text-sm font-ui font-medium text-neutral-700 mb-1 md:mb-2 block">
                    {t("email")}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    required
                    className="px-3 md:px-4 py-2 md:py-3 rounded-lg text-sm"
                    data-testid="form-input-email"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-xs md:text-sm font-ui font-medium text-neutral-700 mb-1 md:mb-2 block">
                    {t("phone")}
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value)}
                    required
                    className="px-3 md:px-4 py-2 md:py-3 rounded-lg text-sm"
                    data-testid="form-input-phone"
                  />
                </div>
              </div>

              <div className="mb-4 md:mb-6">
                <Label htmlFor="requests" className="text-xs md:text-sm font-ui font-medium text-neutral-700 mb-1 md:mb-2 block">
                  {t("specialRequests")}
                </Label>
                <Textarea
                  id="requests"
                  value={specialRequests}
                  onChange={(e) => setSpecialRequests(e.target.value)}
                  className="px-3 md:px-4 py-2 md:py-3 rounded-lg min-h-[80px] md:min-h-[120px] text-sm"
                  data-testid="form-textarea-requests"
                />
              </div>

              <Button
                type="submit"
                disabled={loading || (priceInfo && !priceInfo.meetsMinimum)}
                className="w-full bg-primary hover:bg-primary-600 hover:text-white text-white px-6 md:px-8 py-3 md:py-4 rounded-full font-ui font-medium tracking-wide shadow-lg shadow-primary-200/50 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base"
                data-testid="booking-submit-button"
              >
                {loading ? t("sendingRequest") : priceInfo ? t("bookNowPrice", { price: priceInfo.totalPrice.toFixed(0) }) : t("submitBookingRequest")}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 md:py-20 lg:py-32 bg-muted/30" data-testid="contact-section">
        <div className="max-w-2xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-8 md:mb-12">
            <MessageCircle className="w-8 h-8 md:w-10 md:h-10 text-primary mx-auto mb-3" />
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold tracking-tight">
              {language === "is" ? "Hafa samband" : language === "lt" ? "Susisiekite" : "Contact Us"}
            </h3>
            <p className="text-sm md:text-base text-muted-foreground mt-2">
              {language === "is" ? "Sendu okkur fyrirspurn og við svörum eins fljótt og hægt er" : language === "lt" ? "Siųskite mums užklausą" : "Send us an inquiry and we'll get back to you"}
            </p>
            {property.contact_phone && (
              <a
                href={`tel:${property.contact_phone.replace(/\s/g, "")}`}
                className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-primary/10 text-primary font-ui font-medium rounded-full hover:bg-primary/20 transition-colors text-sm md:text-base"
                data-testid="contact-phone-link"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                {property.contact_phone}
              </a>
            )}
          </div>
          <form onSubmit={handleContact} className="bg-white border border-neutral-200 rounded-xl p-6 md:p-8 space-y-4" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact-name" className="text-sm font-ui font-medium text-neutral-700 mb-1.5 block">
                  {language === "is" ? "Nafn" : language === "lt" ? "Vardas" : "Name"}
                </Label>
                <Input
                  id="contact-name"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  placeholder={language === "is" ? "Fullt nafn" : "Full name"}
                  required
                  className="px-4 py-2.5 rounded-lg"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <Label htmlFor="contact-email" className="text-sm font-ui font-medium text-neutral-700 mb-1.5 block">
                  {language === "is" ? "Netfang" : "Email"}
                </Label>
                <Input
                  id="contact-email"
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  placeholder="email@example.com"
                  required
                  className="px-4 py-2.5 rounded-lg"
                  data-testid="contact-email-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="contact-message" className="text-sm font-ui font-medium text-neutral-700 mb-1.5 block">
                {language === "is" ? "Skilaboð" : language === "lt" ? "Žinutė" : "Message"}
              </Label>
              <Textarea
                id="contact-message"
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                placeholder={language === "is" ? "Skrifaðu fyrirspurn þína hér..." : "Write your message here..."}
                required
                rows={4}
                className="px-4 py-2.5 rounded-lg"
                data-testid="contact-message-input"
              />
            </div>
            <Button
              type="submit"
              disabled={sendingContact}
              className="w-full bg-primary hover:bg-primary-600 hover:text-white text-white px-6 py-3 rounded-full font-ui font-medium tracking-wide shadow-lg shadow-primary-200/50 hover:shadow-xl transition-all duration-200"
              data-testid="contact-submit-button"
            >
              <Send className="w-4 h-4 mr-2" />
              {sendingContact 
                ? (language === "is" ? "Sendi..." : "Sending...") 
                : (language === "is" ? "Senda skilaboð" : language === "lt" ? "Siųsti žinutę" : "Send Message")}
            </Button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-neutral-900 text-neutral-100 py-10 md:py-16 lg:py-20 border-t border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 text-center">
          <h4 className="text-lg md:text-2xl font-display font-semibold mb-2 md:mb-4 line-clamp-2">{property.name}</h4>
          <p className="text-sm md:text-base text-neutral-400 font-body">{property.location}</p>
          <p className="text-xs md:text-sm text-neutral-500 font-body mt-4 md:mt-8">© 2025 {t("allRightsReserved")}</p>
        </div>
      </footer>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={(open) => {
        if (!open && !processingPayment) {
          setShowPaymentModal(false);
          setPaymentMethod(null);
          setBankInfo(null);
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-display">
              {bankInfo ? (language === "is" ? "Millifærsluupplýsingar" : language === "lt" ? "Banko pervedimo informacija" : "Bank Transfer Details") 
                : (language === "is" ? "Veldu greiðslumáta" : language === "lt" ? "Pasirinkite mokėjimo būdą" : "Choose Payment Method")}
            </DialogTitle>
          </DialogHeader>
          
          {!paymentMethod && !bankInfo && (
            <div className="space-y-4 mt-4">
              {currentBooking && (
                <div className="bg-primary-50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-neutral-600 mb-1">
                    {language === "is" ? "Heildarverð" : language === "lt" ? "Bendra kaina" : "Total Price"}
                  </p>
                  <p className="text-2xl font-display font-bold text-primary-600">
                    €{currentBooking.total_price}
                  </p>
                </div>
              )}

              {!paymentConfig.stripe_enabled && !paymentConfig.bank_transfer_enabled && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
                  {language === "is" ? "Engin greiðsluleið er virk. Hafðu samband við eiganda." : "No payment method is currently available. Please contact the owner."}
                </div>
              )}
              
              {paymentConfig.stripe_enabled && (
              <Button
                onClick={handleCardPayment}
                disabled={processingPayment}
                className="w-full h-auto py-4 flex items-center gap-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                data-testid="pay-card-button"
              >
                {processingPayment ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <CreditCard className="w-6 h-6" />
                )}
                <div className="text-left">
                  <div className="font-semibold">
                    {language === "is" ? "Greiða með korti" : language === "lt" ? "Mokėti kortele" : "Pay with Card"}
                  </div>
                  <div className="text-xs opacity-80">Visa, Mastercard, etc.</div>
                </div>
              </Button>
              )}
              
              {paymentConfig.bank_transfer_enabled && (
              <Button
                onClick={handleBankTransfer}
                disabled={processingPayment}
                variant="outline"
                className="w-full h-auto py-4 flex items-center gap-4 border-2"
                data-testid="pay-bank-button"
              >
                {processingPayment ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <Building2 className="w-6 h-6" />
                )}
                <div className="text-left">
                  <div className="font-semibold">
                    {language === "is" ? "Millifærsla" : language === "lt" ? "Banko pavedimas" : "Bank Transfer"}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {language === "is" ? "Greiða með millifærslu" : language === "lt" ? "Mokėti banko pavedimu" : "Pay via bank transfer"}
                  </div>
                </div>
              </Button>
              )}
            </div>
          )}
          
          {bankInfo && (
            <div className="space-y-4 mt-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  {language === "is" 
                    ? "Vinsamlegast millifærðu upphæðina og notaðu tilvísunarnúmerið. Bókunin verður staðfest þegar greiðsla berst."
                    : language === "lt"
                      ? "Prašome pervesti sumą ir nurodyti nuorodos numerį. Rezervacija bus patvirtinta gavus mokėjimą."
                      : "Please transfer the amount using the reference number. Your booking will be confirmed once payment is received."}
                </p>
              </div>
              
              <div className="bg-primary-50 rounded-lg p-4">
                <p className="text-sm text-neutral-600 mb-1">
                  {language === "is" ? "Upphæð til greiðslu" : language === "lt" ? "Suma mokėjimui" : "Amount to Pay"}
                </p>
                <p className="text-2xl font-display font-bold text-primary-600">
                  €{bankInfo.amount} {bankInfo.currency}
                </p>
              </div>

              <div className="space-y-3">
                {[
                  { label: language === "is" ? "Banki" : language === "lt" ? "Bankas" : "Bank", value: bankInfo.bank_name, field: "bank" },
                  { label: language === "is" ? "Reikningseigandi" : language === "lt" ? "Sąskaitos savininkas" : "Account Holder", value: bankInfo.account_holder, field: "holder" },
                  { label: "IBAN", value: bankInfo.iban, field: "iban" },
                  { label: "SWIFT/BIC", value: bankInfo.swift, field: "swift" },
                  { label: language === "is" ? "Tilvísun" : language === "lt" ? "Nuoroda" : "Reference", value: bankInfo.reference, field: "reference", important: true },
                ].map((item) => (
                  <div key={item.field} className={`flex items-center justify-between p-3 rounded-lg ${item.important ? 'bg-green-50 border-2 border-green-200' : 'bg-neutral-50'}`}>
                    <div>
                      <p className="text-xs text-neutral-500">{item.label}</p>
                      <p className={`font-mono ${item.important ? 'text-green-700 font-bold' : ''}`}>{item.value}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(item.value, item.field)}
                      className="shrink-0"
                    >
                      {copiedField === item.field ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                ))}
              </div>

              <Button
                onClick={resetBookingForm}
                className="w-full mt-4"
              >
                {language === "is" ? "Loka" : language === "lt" ? "Uždaryti" : "Close"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomePage;